import { Component, Input, OnInit, OnDestroy, Optional, Self, HostBinding, ElementRef, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Observable, Subject } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';

@Component({
    selector: 'app-virtual-drop-down',
    templateUrl: './virtual-drop-down.component.html',
    styleUrls: ['./virtual-drop-down.component.scss'],
    providers: [
        { provide: MatFormFieldControl, useExisting: VirtualDropDownComponent }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VirtualDropDownComponent implements OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string> {
  static nextId = 0;
  @HostBinding() id = `virtual-drop-down-${VirtualDropDownComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedBy = '';

  stateChanges = new Subject<void>();
  focused = false;
  controlType = 'virtual-drop-down';

  @Input() bindLabel: string;
  @Input() bindValue: any;
  @Input() items: Observable<any[]>;
  @Input() dropDownOptions: any;

  @Output() selectedItemChange = new EventEmitter<any>();

  _selectedValue: any;
  searchTerm: any;

  @Input()
  public get selectedValue(): any { 
    return this._selectedValue;
  }
  public set selectedValue(value: any) {
    this._selectedValue = value;
    this.onChange(value);
    this.onTouch();
    this.stateChanges.next();
    this.selectedItemChange.emit(value);
  }

  @Input()
  get value(): any {
    return this.selectedValue;
  }
  set value(val: any) {
    this.selectedValue = val;
    this.stateChanges.next();
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(plh: string) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  private _placeholder: string;

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(req: boolean) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(dis: boolean) {
    this._disabled = coerceBooleanProperty(dis);
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get clearable(): boolean {
    return this._clearable;
  }
  set clearable(value: boolean) {
    this._clearable = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _clearable = false;

  get empty() {
    return !this.selectedValue;
  }

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get errorState(): boolean {
    return this.ngControl && this.ngControl.invalid && (this.ngControl.dirty || this.ngControl.touched);
  }

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private elementRef: ElementRef,
    private fm: FocusMonitor
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    fm.monitor(this.elementRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;
      this.stateChanges.next();
    });
  }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elementRef.nativeElement);
  }

  onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      const dxInput: HTMLInputElement | null = this.elementRef.nativeElement.querySelector('.dx-texteditor-input');
      if (dxInput) {
        dxInput.focus();
        return;
      }
      this.elementRef.nativeElement.querySelector('input')?.focus();
    }
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  writeValue(value: any): void {
    this.selectedValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.stateChanges.next();
  }

  private onChange: (value: any) => void = () => { };
  private onTouch: () => void = () => { };

  externalSearchTermChanged($event: any) {
    this.searchTerm = $event.data;
  }

  focus() {
    this.focused = true;
    this.stateChanges.next();
  }

  blur() {
    this.focused = false;
    this.onTouch();
    this.stateChanges.next();
  }

  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
    this.onChange(input.value);
    this.onTouch();
    this.stateChanges.next();
  }

 
}
